import { Box, Button } from '@amzn/awsui-components-react';
import { capitalize, snakeCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { AuthRole } from '../../../../constants/shared/auth-roles';
import { CognitoUser } from '../../../../types/User';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { getTableFilterMatcher, TableFilterMatcher } from '../../../../utils/table.utils';

const formatStatus = (status: string): string => {
  return snakeCase(status).split('_').map(capitalize).join(' ');
};

export const getGroupTitle = (group: string): string => {
  if (isCustomGroup(group)) {
    return formatStatus(group);
  }

  return group.split('-').map(capitalize).join(' ');
};

const isCustomGroup = (group: string): boolean => {
  return !(Object.values(AuthRole) as string[]).includes(group);
};

export const filteringFunction = (item: CognitoUser, filteringText: string): boolean => {
  const matcher: TableFilterMatcher = getTableFilterMatcher(filteringText);
  return (
    matcher.filterEmpty ||
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    matcher.isMatch(item.email) ||
    matcher.isMatch(item.cognitoUsername) ||
    (item.roles?.findIndex((group) => getGroupTitle(group).includes(filteringText)) || -1) > -1 ||
    matcher.isMatch(formatStatus(item.status))
  );
};

export const COLUMN_DEFINITIONS = ({ setSelectedUser }: { setSelectedUser: (user: CognitoUser) => void }) => {
  const { t } = useTranslation();

  return [
    {
      id: 'login',
      header: t(i18nKeys.settings.userManagement.table.headers.username),
      cell: (item: CognitoUser) => item?.login ?? item?.legacyLogin,
    },
    {
      id: 'provider',
      header: t(i18nKeys.settings.userManagement.labels.provider),
      cell: (item: CognitoUser) => item.publicProviderName ?? item.providerName,
    },
    {
      id: 'email',
      header: t(i18nKeys.settings.userManagement.table.headers.email),
      cell: (item: CognitoUser) => <Box color="text-status-info">{item.email}</Box>,
    },
    {
      id: 'vibeID',
      header: t(i18nKeys.settings.userManagement.labels.vibeId),
      cell: (item: CognitoUser) => item?.vibeId,
    },
    {
      id: 'actions',
      header: t(i18nKeys.settings.userManagement.table.headers.actions),
      cell: (item: CognitoUser) => (
        <Button variant="inline-link" onClick={() => setSelectedUser(item)}>
          {t(i18nKeys.settings.userManagement.buttons.seeDetails)}
        </Button>
      ),
    },
  ];
};
